import type {
  Database,
  DatabaseAccessGrant,
  GrantDatabaseAccessRequest,
  TenantOwnedDatabase
} from '@gat-ship/types'

export class DatabaseAccessUtils {
  /**
   * Finds the access grant for a database, if the user has one.
   * @param databaseGrants - The database grants of the tenant user to search.
   * @param tenantDatabaseId - The id of the tenant database to look up.
   * @returns The matching grant, or `undefined` when none exists.
   */
  static findGrantForDatabase(
    databaseGrants: readonly DatabaseAccessGrant[],
    tenantDatabaseId: string
  ): DatabaseAccessGrant | undefined {
    return databaseGrants.find((i) => i.databaseId === tenantDatabaseId)
  }

  /**
   * Checks if a user has granted database access.
   * @param tenantDatabaseId - The id of the tenant database to check.
   * @param databaseGrants - The database grants of the tenant user to check.
   * @returns `true` if the user has granted the database access, `false` otherwise.
   */
  static isGrantedDatabaseAccess(
    tenantDatabaseId: string,
    databaseGrants: DatabaseAccessGrant[]
  ) {
    return (
      this.findGrantForDatabase(databaseGrants, tenantDatabaseId) !== undefined
    )
  }

  /**
   * Type guard: a grant-database-access request is valid once it carries both a
   * `userId` and a `databaseId`.
   */
  static isValidGrantDatabaseAccessRequest(
    payload: Partial<GrantDatabaseAccessRequest>
  ): payload is GrantDatabaseAccessRequest {
    return Boolean(payload.userId && payload.databaseId)
  }

  /**
   * The owned databases visible for a tenant user: the full set for the current
   * user, otherwise only those the current user can also access. Returns a fresh
   * array.
   *
   * @param userId - The tenant user whose databases are being listed.
   * @param currentUserId - The id of the signed-in user.
   * @param allTenantDatabases - Every database owned by the tenant.
   * @param currentUserDatabases - The databases the current user can access.
   */
  static selectOwnedDatabasesForUser(
    userId: string,
    currentUserId: string,
    allTenantDatabases: readonly TenantOwnedDatabase[],
    currentUserDatabases: readonly { id: string }[]
  ): TenantOwnedDatabase[] {
    if (userId === currentUserId) {
      return [...allTenantDatabases]
    }
    return allTenantDatabases.filter((tenantOwnedDb) =>
      currentUserDatabases.some((db) => db.id === tenantOwnedDb.id)
    )
  }

  /**
   * Builds the list of databases a user can access by joining the user's grants
   * with the owned-database details and the current database context. Grants
   * without a matching owned database are dropped.
   *
   * @param databaseGrants - The user's database access grants.
   * @param ownedDatabases - The owned databases visible for the user.
   * @param currentDatabase - The currently used database, for selection/tenant context.
   */
  static buildAccessibleDatabases(
    databaseGrants: readonly DatabaseAccessGrant[],
    ownedDatabases: readonly TenantOwnedDatabase[],
    currentDatabase:
      | { id: string; tenantId: string; tenantName: string }
      | undefined
  ): Database[] {
    return databaseGrants
      .map((accessDbGrant): Database | null => {
        const found = ownedDatabases.find(
          (db) => db.id === accessDbGrant.databaseId
        )
        if (!found) {
          return null
        }
        return {
          ...found,
          id: accessDbGrant.databaseId,
          isSelected: currentDatabase?.id === accessDbGrant.databaseId,
          tenantId: currentDatabase?.tenantId ?? '',
          tenantName: currentDatabase?.tenantName ?? '',
          affectedBySupportSession: false,
          isDefault: false
        }
      })
      .filter((db): db is Database => db !== null)
  }

  /**
   * Picks the database to pre-select: the current database when it is in the
   * list, otherwise the first one, or `undefined` when the list is empty.
   */
  static selectDefaultDatabaseId(
    databases: readonly Database[],
    currentDatabaseId: string | undefined
  ): string | undefined {
    if (databases.length === 0) {
      return undefined
    }
    if (databases.some((db) => db.id === currentDatabaseId)) {
      return currentDatabaseId
    }
    // Length check above guarantees this exists.
    return databases[0]!.id
  }

  /**
   * Checks if a user can revoke database access.
   * @param databaseAccessId - The id of the database access to revoke.
   * @param tenantUserId - The id of the tenant user to revoke the database access from.
   * @param currentUsedDatabaseId - The id of the database currently used by the user.
   * @param userId - The id of the user who is revoking the database access.
   * @returns `true` if the user can revoke the database access, `false` otherwise.
   */
  static canRevokeDatabaseAccess(
    /** The id of the database access to revoke. */
    databaseAccessId: string,
    /** The id of the tenant user to revoke the database access from. */
    tenantUserId: string,
    /** The id of the database currently used by the user. */
    currentUsedDatabaseId: string | undefined,
    /** The id of the user who is revoking the database access. */
    userId: string
  ) {
    if (!currentUsedDatabaseId || !userId || !tenantUserId) {
      return true
    }
    const isSameUser = userId === tenantUserId
    const isCurrentlyUsedDatabase = currentUsedDatabaseId === databaseAccessId
    return !(isSameUser && isCurrentlyUsedDatabase)
  }
}
